/**
 * Módulo 4 (final) del tutorial: El mapa de tu campo (KAN-232).
 *
 * Tour completo del Mapa del Campo: entrada por el menú Principal, botones
 * de capa base (clic en Esri), foto satelital + Viaje Atrás (Wayback),
 * puesto, dibujo de cuadros/corrales/aguadas, panel de Capas (cobertura de
 * agua, NDVI con su generación diferida), qué datos da (raciones por
 * cuadro, ficha), capa de Relieve (pendientes/cañerías) y mediciones.
 * Reusa el flujo ?accion=poner-puesto para el paso del puesto.
 *
 * Anclas: menu-mapa (ítem "Mapa" del menú Principal, ruta /mapa),
 * base-satelite (selector de capa base), dibujar-cuadro/dibujar-corral/
 * dibujar-aguada (toolbar), btn-capas, capa-aguas-cobertura,
 * capa-ndvi-toggle, base-relieve, btn-medir.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { getRepositories } from '../../../services/storage';

const MAPA = '/mapa';

export const puestoMapaModule: TutorialModuleDef = {
  id: 'puesto-mapa',
  title: 'El mapa de tu campo',
  description: 'Puesto, cuadros, corrales y aguadas sobre la foto satelital. Capas de vegetación (NDVI), cobertura de agua, relieve y mediciones.',
  emoji: '🗺️',
  image: 'images/tutorial/puesto-mapa.png',
  permission: { module: 'map', action: 'create' },
  grupo: 'esencial',
  route: MAPA,
  steps: [
    {
      id: 'concepto-mapa',
      route: '/tutorial',
      text: 'Tu campo, visto desde el cielo. Dibujá puesto, cuadros y aguadas sobre la foto satelital, y la app mide hectáreas, vegetación y raciones sola.',
      narration:
        'Vamos con lo más lindo: el mapa de tu campo. Es una foto satelital donde dibujás tu puesto, tus cuadros, tus aguadas, y la app mide sola las hectáreas, mira el verde del pasto desde el cielo, y te estima cuántas raciones da cada cuadro. Todo un libro de campo inteligente. Empecemos.',
    },
    {
      id: 'ir-al-mapa',
      target: '[data-ar-guide="menu-mapa"]',
      label: 'Mapa (menú Principal)',
      text: 'En el menú, sección Principal, tocá "Mapa" (en celular: menú ☰).',
      narration:
        'Andá al menú, y en la sección Principal ya tenés el Mapa a mano, sin vueltas. Tocá y esperá un cachito que cargue la foto satelital de tu campo.',
    },
    {
      id: 'capas-base',
      route: MAPA,
      target: '[data-ar-guide="base-satelite"]',
      label: '🛰 Esri Satelital',
      text: 'Arriba del mapa, a la derecha, tenés los botones de capa: Google, Esri, Callejero y Relieve. Tocá "Esri Satelital" — con esa foto funciona todo lo que sigue.',
      narration:
        'Antes de arrancar, mirá arriba del mapa, a la derecha: ahí están los botones de capa. Google, Esri Satelital, Callejero y Relieve. Tocá Esri Satelital, que es la foto con la que funciona todo lo que viene.',
    },
    {
      id: 'el-mapa-wayback',
      route: MAPA,
      target: '[data-ar-guide="mapa-wayback"]',
      label: '🕰️ Viaje Atrás (arriba a la izquierda)',
      advanceOnClick: false,
      text: 'Con Esri activo, arriba a la izquierda aparece el 🕰️ "Viaje Atrás" (te lo señalo): elegís un año y ves la foto satelital de ese entonces — hasta la reproducís como película desde 2014.',
      narration:
        'Y ahora que estás en Esri, arriba a la izquierda te aparece el Viaje Atrás, ese control que te marco. Es la máquina del tiempo: elegís un año y ves la foto satelital de ese entonces. Y si apretás play, tu campo se vuelve una película, año por año, desde el dos mil catorce. Para ver crecer los montes o acordarte dónde estaba algo.',
    },
    {
      id: 'poner-puesto',
      route: `${MAPA}?accion=poner-puesto`,
      text: 'Ya te dejamos el modo "Puesto" activado: hacé UN clic donde está tu casa o casco, y confirmá.',
      narration:
        'Primero lo primero: tu puesto. Ya te dejé listo el modo puesto, así que hacé un solo clic justo donde está la casa o el casco del campo. Ese punto es tu referencia para todo lo demás.',
    },
    {
      id: 'dibujar-cuadro',
      route: MAPA,
      target: '[data-testid="dibujar-cuadro"]',
      label: '🟩 Cuadro',
      advanceOnClick: false,
      text: 'Tocá "Cuadro" y dibujalo con clics: cada clic marca una esquina, ENTER o doble clic para terminar. Las hectáreas se calculan solas — y si queda un hueco chico con el vecino, se apoya solo.',
      narration:
        'Ahora los cuadros, que son el corazón de todo. Tocá el botón Cuadro y marcá las esquinas de a un clic por vez; cuando cerrás, apretá enter o doble clic y listo. Fijate que las hectáreas salen solas, sin agrimensor. Y un detalle cómodo: si el cuadro te queda pegado a otro con un hueco chico de menos de cincuenta metros, la app lo apoya sola contra el vecino, no hace falta dibujar milimétrico. Los corrales se dibujan igual, pero de eso en un momento.',
    },
    {
      id: 'guardar-vincular',
      route: MAPA,
      text: 'Al cerrar el dibujo, elegí del listado la ubicación de este cuadro: nombre y vínculo salen solos. Si no existe, creala ahí mismo, con su campo — y si no tenés campos, también los creás en el momento.',
      narration:
        'Al cerrar el dibujo te preguntamos qué ubicación es este cuadro. Si ya la cargaste antes, la elegís del listado y listo: nombre y vínculo salen solos, y los animales de esa ubicación aparecen en el mapa. Y si no existe, la creás ahí mismo: le ponés nombre, elegís a qué campo pertenece, y si todavía no tenés ningún campo, lo creás sin salir de acá. Todo queda conectado: campo, ubicación y mapa.',
    },
    {
      id: 'dibujar-corral',
      route: MAPA,
      target: '[data-testid="dibujar-corral"]',
      label: '🟫 Corral',
      advanceOnClick: false,
      text: 'Los corrales se dibujan igual que los cuadros. Acordate: corral = engorde a balanceado, cuadro = pastura.',
      narration:
        'Los corrales van igual: botón Corral, esquinas con clics, doble clic y chau. Y te acordás de la diferencia del módulo anterior: el corral es engorde a balanceado, el cuadro es pastura. Contáselo bien a la app, que de eso dependen las cuentas.',
    },
    {
      id: 'dibujar-aguada',
      route: MAPA,
      target: '[data-testid="dibujar-aguada"]',
      label: '💧 Aguada',
      advanceOnClick: false,
      text: 'Tocá "Aguada" y marcá con UN clic dónde está el pozo o bebedero. Círculo chico: zona de uso pleno. Círculo grande: distancia máxima efectiva — mientras más lejos, menos se aprovecha el sector.',
      narration:
        'Ahora las aguadas, que son oro para las cuentas. Tocá Aguada y hacé un clic donde está el pozo o el bebedero. Fijate los dos círculos: el chico marca la zona de uso pleno, donde el animal come tranquilo; el grande marca la distancia máxima efectiva, y mientras más lejos del agua, menos se aprovecha ese sector, porque el bicho gasta energía yendo y viniendo, y esa energía no se convierte en kilo. De más está decir que pegado al agua se sobrepastorea, y lejos queda pasto sin usar. Por eso la aguada no cambia cuánto pasto tiene la hectárea: cambia cuánto está al alcance fácil del animal, y con eso, cuánta utilización efectiva podés hacer del cuadro. Y esa utilización mueve la estimación de raciones: con y sin aguada, las cuentas cambian solas.',
    },
    {
      id: 'aguada-cuadros-acceso',
      route: MAPA,
      target: '[data-ar-guide="aguada-conexion-manual"]',
      extraTargets: ['[data-ar-guide="aguada-conexion-auto"]'],
      advanceTargets: ['[data-ar-guide="modal-elemento-guardar"]'],
      label: '¿A qué cuadros les da agua?',
      advanceOnClick: false,
      text: 'Cuando la aguada alcanza a más de un cuadro, deciles a CUÁLES de verdad: tocá "Sólo a los que marque" — la app pre-marca los que alcanza por distancia y vos DESMARCÁS los que el ganado no puede llegar (alambrado, callejón). Cuando estés, GUARDÁ la aguada y seguimos.',
      narration:
        'Detalle fino y importante del cuadro de la aguada: esta pregunta de a qué cuadros les da agua. Por defecto es automático: todo lo que alcanza por distancia. Pero la distancia no sabe de alambrados. Si el ganado de un cuadro vecino no puede cruzar a tomar agua, tocá Sólo a los que marque: la app te pre-marca los cuadros que alcanzaría, y vos le desmarcás los que en la realidad no llegan, por un alambre, por un callejón, por lo que sea. Cuando termines, guardá la aguada y seguimos.',
    },
    {
      id: 'aguada-por-que',
      route: MAPA,
      advanceTargets: ['[data-ar-guide="modal-elemento-guardar"]'],
      text: '¿Por qué importa? Una aguada no cambia cuánto pasto da la hectárea: cambia CUÁNTAS hectáreas se usan. Marcar bien los cuadros con acceso hace exacta la capacidad sustentable de cada uno — y te avisa si un cuadro quedó sobrecargado. Guardá la aguada cuando estés.',
      narration:
        '¿Y por tanto lío por unos cuadros? Porque una aguada no cambia cuánto pasto da la hectárea: cambia cuántas hectáreas se usan de verdad. Si la app cree que un cuadro tiene agua y no la tiene, te calcula de más la capacidad, y el pasto te lo dice al ñudo al año siguiente. Con los cuadros bien marcados, la capacidad sustentable de cada uno sale exacta, la presión de carga te avisa cuando te pasás, y las decisiones de dónde poner hacienda se toman con datos, no con esperanzas. Guardá la aguada y seguimos.',
    },
    {
      id: 'panel-capas',
      route: MAPA,
      target: '[data-testid="btn-capas"]',
      label: '🗂 Capas',
      text: 'Tocá "Capas" para encender y apagar lo que ves: cuadros, instalaciones, anotaciones, ecosistemas, lluvia satelital y más.',
      narration:
        'Este botón de Capas es el tablero de luces del mapa. Tocándolo prendés y apagás lo que quieras ver: los cuadros, las instalaciones, las anotaciones, los ecosistemas, y hasta la lluvia de cada año medida por satélite. Prendé lo que necesites y apagá lo que estorbe.',
    },
    {
      id: 'cobertura-agua',
      route: MAPA,
      target: '[data-ar-guide="capa-aguas-cobertura"]',
      label: 'Cobertura de agua',
      advanceOnClick: false,
      text: 'Con aguadas cargadas, en Capas podés prender "Cobertura de agua": ves el gradiente de acceso sobre tus cuadros — pleno hasta 1,6 km del agua, decae hasta los 3,2 km.',
      narration:
        'Con tus aguadas cargadas, en el panel de capas prendés Cobertura de agua y mirá el gradiente encima de los cuadros: verde pleno hasta el kilómetro y medio del agua, y de ahí va apagándose hasta poco más de tres kilómetros, donde ya no cuenta. Una belleza para ver qué rincones quedan mal servidos y decidir dónde conviene el próximo bebedero.',
    },
    {
      id: 'capa-ndvi',
      route: MAPA,
      target: '[data-ar-guide="capa-ndvi-toggle"]',
      extraTargets: ['[data-ar-guide="ndvi-generando"]'],
      label: '🌿 Vegetación',
      advanceOnClick: false,
      text: 'Prendé "Vegetación (NDVI)": la imagen de fotosíntesis de tu campo. ¿Ves la barra de abajo? Está GENERÁNDOSE sola desde que dibujaste tus cuadros — cuando termina tenés la estimación y toda la información de cada mes. La imagen del mes sale en minutos; el historial completo se termina de hornear en el día (vas a ver el avance debajo del mapa).',
      narration:
        'Y acá viene la perla del sistema: la capa de Vegetación. Es una imagen de fotosíntesis de tu campo, tomada por satélite cada mes. ¿Ves esa barra que te marco abajo del mapa? Es la generación en marcha: desde que dibujaste tus cuadros, el sistema está armando todo el histórico, mes por mes. Cuando termina, tenés la imagen, la estimación de cada cuadro y toda la información para comparar. La imagen del mes ya la vas a tener en minutos, y el historial completo se termina de hornear en el día (mirá el avance debajo del mapa). Después, cada mes nuevo se agrega solo. Con las flechitas recorrés mes a mes, y comparás contra el año pasado para ver si el campo viene mejor o peor que siempre.',
    },
    {
      id: 'datos-ndvi',
      route: MAPA,
      text: 'Con NDVI activo, cada cuadro te dice su verde vs. su histórico propio, y la tabla "Raciones y vegetación por cuadro" estima raciones/año y raciones por hectárea. Tocá un cuadro para la ficha completa con el "cómo se llega a ese número".',
      narration:
        '¿Y qué te da todo esto? Pasá el mouse por un cuadro y te cuenta su verde, comparado con su propio histórico, no con el del vecino. Abajo, la tabla de raciones y vegetación estima cuántas raciones al año da cada cuadro y cuántas por hectárea, mezclando el verde satelital, la lluvia que vos registrás y el alcance de las aguadas. Y si tocás el cuadro, se abre la ficha completa, con el recibo paso a paso de cómo se llega a ese número. Nada de cajas negras.',
    },
    {
      id: 'relieve',
      route: MAPA,
      target: '[data-ar-guide="base-relieve"]',
      label: '⛰️ Relieve',
      advanceOnClick: false,
      text: 'Cambiá la capa base a "⛰️ Relieve": curvas de nivel y la altura siguiendo al cursor. Y para saber si el agua LLEGA sola de una aguada a otra, está la herramienta "🚰 Tubería" (botón de arriba): le marcás fuente y destino y te dice si cae, con qué caño y con qué bomba.',
      narration:
        'Otra joya: la capa de Relieve. Cambiá la vista a Relieve y vas a ver las curvas de nivel del terreno, y la altura te sigue al cursor mientras lo movés. ¿Para qué sirve? Para pensar con la panza del terreno. Y cuando quieras respuestas con números, usá la herramienta Tubería, ese botón del panel de arriba: le marcás de dónde sale el agua y a dónde la querés llevar, y te dice si llega cayendo sola, con qué caño, o qué bomba necesitás si hay que empujarla. El campo te habla, hay que saber leerlo.',
    },
    {
      id: 'pantalla-completa',
      route: MAPA,
      target: '[data-testid="btn-pantalla-completa"]',
      label: '⛶ Ampliar',
      advanceOnClick: false,
      text: '¿Mucho campo para una pantalla chica? Tocá "⛶ Ampliar" (sobre el mapa, junto a Capas): el mapa ocupa todo, con los botones arriba y abajo tus números — hectáreas, cuadros, aguadas y las raciones. Salís con ✕ o con Esc.',
      narration:
        'Un último comodín antes del cierre: el botón de Ampliar, ese que está sobre el mapa, al lado de Capas. Tocás, y el mapa se planta a pantalla completa: arriba tenés todos los botones de dibujo, y abajo tus números del campo siempre a la vista: hectáreas, cuadros, aguadas, y lo que tus animales vienen comiendo. Para volver al tamaño normal, el botón de salir, o la tecla escape.',
    },
    {
      id: 'medir-despedida',
      route: MAPA,
      target: '[data-testid="btn-medir"]',
      label: '📏 Medir distancia',
      advanceOnClick: false,
      text: 'Con "📏 Medir distancia" y "📐 Medir área" medís cualquier cosa sobre el mapa. Y si te quedan dudas, el botón ❓ de Ayuda te explica todo. ¡Fin del tutorial!',
      narration:
        'Y para cerrar, dos herramientas de bolsillo: medir distancia y medir área, para cuando quieras saber cuánto hay de acá a allá sin tomar la camioneta. Si te quedan ganas de saber más, el botón de Ayuda del mapa te explica todo con detalle. Esto era lo último del tutorial: ya tenés tu campo completo andando. ¡Buenas, y que Dios te multiplique el rodeo, criollo!',
    },
  ],
  completionCheck: async () => {
    const els = (await getRepositories().elementoMapa.getAllForActiveEmpresa()) as { tipo: string }[];
    return els.some(e => e.tipo === 'PUESTO');
  },
};
